import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'cta';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
  fullWidth?: boolean;
};

const base =
  'inline-flex min-h-touch items-center justify-center gap-2 rounded-pill px-6 text-lg font-semibold transition-[transform,background-color,opacity] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100';

const variants: Record<Variant, string> = {
  primary: 'bg-sakura-deep text-ink-on-teal active:bg-espresso',
  secondary: 'border-2 border-hairline bg-surface text-espresso active:bg-cream-deep',
  tertiary: 'bg-transparent text-sage-deep underline-offset-4 active:underline',
  destructive: 'bg-state-danger text-ink-on-teal',
  cta: 'bg-cta-green text-ink-on-teal active:bg-cta-green-deep',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, fullWidth = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
